import {InputNumber} from "primereact/inputnumber";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";

import {formatParameterValue} from "@reva/shared/number-format";

import {useRevaTranslation} from "../../hooks/languageHooks";
import {navigateToRouteAndHash} from "../../hooks/navigationHooks";
import {selectAssociatedOutputParamOutputCurried} from "../../redux/objectParametersSelectors";
import {ALL_SOURCES_TO_ROUTE} from "../../redux/objectParametersSlice";
import {updateParamAndValidateAndSubmitFormThunk} from "../../redux/objectParametersThunk";
import InputNumberConflictOptions from "./InputNumberConflictOptions";
import {MeasurementDialogIcon} from "./MeasurementDialogIcon";
import ParameterFieldContainer from "./ParameterFieldContainer";
import {getResourceColor, useInputParameterDef} from "./paramHooks";

/**
 * Input for number parameters (Integer or Double).
 * @param {string} [parameter] parameter key (can be omitted, if parameter_details_def with parameter property is passed)
 * @param {RevaValueParameterType} [parameterType] type of parameter e.g. "AVM","META" or "DOC_PROPERTY"
 * @param {string|null} [label] rendered label (override from parameter details)
 * @param {string} id ID of the input
 * @param {InputParameterDetailsDef|null} [parameter_details_def] optional parameter details definition, to use instead of parameter
 * @param {string|null} [associatedOutputParam] optional output parameter name, to display the value of the output parameter
 * @param {RenderInputControlFunction} [renderEditOverlay] optional function to render an overlay to edit the value (this will cause the regular input to be disabled)
 * @return {React.JSX.Element|null}
 */
export default function ParameterInputNumber({
    parameter,
    parameterType,
    label,
    id,
    parameter_details_def,
    associatedOutputParam,
    renderEditOverlay,
    page,
}) {
    const {t, language} = useRevaTranslation();
    const parameterData = useInputParameterDef(parameter, parameter_details_def);
    const associatedOutputParamValue = useSelector(selectAssociatedOutputParamOutputCurried(associatedOutputParam));
    const navigate = useNavigate();
    const dispatch = useDispatch();

    if (!parameterData) return null;

    const hasAssociatedOutput = typeof associatedOutputParamValue === "number";

    const isDouble = parameterData.type === "double";
    const specMinDecimalPlaces = parameterData.formatting?.minDecimalPlaces;
    const specMaxDecimalPlaces = parameterData.formatting?.maxDecimalPlaces;
    const minFractionDigits = isDouble ? (specMinDecimalPlaces ?? 2) : 0;
    let maxFractionDigits = isDouble ? (specMaxDecimalPlaces ?? 2) : 0;
    const options = {omitUnit: true};

    if (parameter === "ownership_share") {
        maxFractionDigits = 4;
        options.formatOverride = "DOUBLE(0-4)";
    }

    const fmtNumber = (value) => formatParameterValue(language, parameterData, value, options);

    let placeholder = hasAssociatedOutput
        ? `${t("object.associatedOutputParamPrefix")} ${fmtNumber(associatedOutputParamValue)}`
        : parameterData?.maxValue
          ? `max. ${fmtNumber(parameterData.maxValue)}`
          : null;

    //#18222 Erbbaurecht in Überblick nicht editierbar
    const forward = parameter.startsWith("prop_leasehold") && parameterType === "AVM";
    const onForward = () =>
        navigateToRouteAndHash(navigate, ALL_SOURCES_TO_ROUTE["register"], "register_leasehold_input");
    const setNullOrEmpty = (value) =>
        dispatch(updateParamAndValidateAndSubmitFormThunk(parameter, "REGISTER", value, null));

    return (
        <ParameterFieldContainer
            id={id}
            label={label}
            parameter={parameter}
            parameterType={parameterType}
            parameter_details_def={parameter_details_def}
            showUnit={true}
            showDescription={true}
            onResetOrEmpty={forward ? setNullOrEmpty : null}
            hasAssociatedOutputParam={!!associatedOutputParam}
            associatedOutputParamValue={associatedOutputParamValue}
            renderAdditionalAction={({onChange, label, disabled}) => {
                if (
                    (parameter === "space_plot" || parameter === "space_plot_backland") &&
                    page === "object/groundvalue"
                ) {
                    return (
                        <MeasurementDialogIcon
                            label={label}
                            disabled={disabled}
                            onConfirm={(value) => {
                                return onChange({target: {value}});
                            }}
                        />
                    );
                }

                return null;
            }}
            renderControl={({className, onChange, value, activeParameterType, hasConflict, disabled, allValues}) => {
                const color = getResourceColor(activeParameterType, hasConflict);
                const filledPlaceholderClass = hasAssociatedOutput ? "indication-filled-placeholder" : "";
                const conflictClassName = hasConflict ? `border-l-0 border-${color}` : "";
                const emptyString = t("object.valueTooltip.revaEmpty");
                placeholder = allValues?.reva === "empty" || value === "empty" ? emptyString : placeholder;

                return (
                    <>
                        <InputNumberConflictOptions
                            {...{allValues, hasConflict, onChange, fmtNumber, emptyString}}
                            className={`${color} ${parameter}`}
                        />
                        <InputNumber
                            inputId={id}
                            className={`w-full${forward ? " cursor-pointer" : ""}`}
                            disabled={disabled || forward}
                            inputClassName={`text-right text-${color} ${className} ${filledPlaceholderClass} ${parameter} ${conflictClassName}`}
                            value={typeof value === "number" ? value : null}
                            locale={language}
                            placeholder={placeholder}
                            useGrouping={true}
                            minFractionDigits={minFractionDigits}
                            maxFractionDigits={maxFractionDigits}
                            onValueChange={onChange}
                            data-pr-position="top"
                            onClick={() => (forward ? onForward() : null)}
                            onBlur={(e) => {
                                if (e.target.value === "" && typeof value === "number") e.target.value = value;
                            }}
                        />
                    </>
                );
            }}
            renderEditOverlay={renderEditOverlay}
        />
    );
}
